import { Color as PersonaColor } from '@persona-id/design-tokens';
import Color from 'color';
import { ComponentPropsWithoutRef } from 'react';
import { fieldStyles } from 'shared/styles/form';
import { COLORS, FONT_WEIGHTS, HEIGHTS, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import IconCaretDown from 'images/icons/caret-down.svg';
import FormControlProps from 'lib/interfaces/FormControlProps';

interface SelectOwnProps {
  isSelectingPlaceholder: boolean;
}

type InnerSelectProps = SelectProps & SelectOwnProps;

const InnerSelect = styled('select')<InnerSelectProps>`
  ${(props) => fieldStyles(props, props.theme)};

  /* Hide default styling */
  appearance: none;
  &::-ms-expand {
    display: none;
  }

  color: ${({ theme }) => theme.colorFont ?? COLORS.gray20};
  height: ${HEIGHTS.input};

  padding-top: ${SPACINGS.base}px;
  padding-bottom: ${SPACINGS.base}px;
  padding-left: ${SPACINGS.base * 2}px;
  // NOTE: this prevents the long template id values from showing up behind the caret
  padding-right: ${SPACINGS.base * 4}px;

  ${({ isSelectingPlaceholder, theme }) =>
    isSelectingPlaceholder &&
    css`
      font-weight: ${FONT_WEIGHTS.regular};
      /* Placeholder styles have opacity 0.5, but we can't only target the placeholder element with <select/> */
      color: ${Color(theme.colorFont ?? COLORS.gray100)
        .fade(0.5)
        .rgb()
        .string()};
    `}

  ${({ hasError, theme, value }) => {
    const textColorRgb = Color(theme.colorError ?? PersonaColor.CoreRed900).rgb();

    return (
      hasError &&
      (value == null || value === '') &&
      css`
        /* Placeholder error styles have opacity 0.7, but we can't only target the placeholder element with <select/> */
        color: ${`rgba(${textColorRgb.red()}, ${textColorRgb.green()}, ${textColorRgb.blue()}, 0.7)`};
        -webkit-text-fill-color: ${`rgba(${textColorRgb.red()}, ${textColorRgb.green()}, ${textColorRgb.blue()}, 0.7)`};
      `
    );
  }}
`;

const Wrapper = styled('div')`
  position: relative;
`;

const StyledIconCaretDown = styled(IconCaretDown)`
  position: absolute;
  right: ${SPACINGS.base * 2}px;
  top: 50%;
  transform: translateY(-50%);

  display: inline-block;
  pointer-events: none;

  rect[fill],
  path[fill] {
    fill: ${({ theme }) => theme.colorFont ?? COLORS.gray50};
  }
`;

export type SelectProps = ComponentPropsWithoutRef<'select'> & FormControlProps;

const Select = ({ value, ...props }: SelectProps) => {
  return (
    <Wrapper>
      <InnerSelect {...props} value={value} isSelectingPlaceholder={value === ''} />
      <StyledIconCaretDown />
    </Wrapper>
  );
};

export default Select;
